import { api } from './api';

let swRegistration = null;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function arrayBufferToBase64Url(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function getFreshSubscription(vapidKey) {
  const desiredKey = (vapidKey || '').replace(/=+$/, '');
  let subscription = await swRegistration.pushManager.getSubscription();
  if (subscription) {
    const currentKey = arrayBufferToBase64Url(subscription.options?.applicationServerKey);
    if (currentKey && desiredKey && currentKey !== desiredKey) {
      try { await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }); } catch { /* ignore stale server unsubscribe */ }
      await subscription.unsubscribe();
      subscription = null;
    }
  }
  if (!subscription) {
    subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  return subscription;
}

export async function initPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push notifications not supported');
    return false;
  }
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return true;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return false;
  }
}

export async function subscribeToPush(role = 'staff') {
  if (!swRegistration) {
    const ok = await initPushNotifications();
    if (!ok) return null;
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const resp = await api.get('/push/vapid-public-key');
    const vapidKey = resp.data.public_key;
    if (!vapidKey) return null;

    const subscription = await getFreshSubscription(vapidKey);

    await api.post('/push/subscribe', {
      subscription: subscription.toJSON(),
      role,
    });

    return subscription;
  } catch (err) {
    console.error('Push subscription failed:', err);
    return null;
  }
}

export async function subscribeGuestPush(hotelId, guestEmail) {
  if (!swRegistration) {
    const ok = await initPushNotifications();
    if (!ok) return null;
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const resp = await api.get('/push/vapid-public-key');
    const vapidKey = resp.data.public_key;
    if (!vapidKey) return null;

    const subscription = await getFreshSubscription(vapidKey);

    await api.post('/push/subscribe-guest', {
      subscription: subscription.toJSON(),
      hotel_id: hotelId,
      guest_email: guestEmail,
    });

    return subscription;
  } catch (err) {
    console.error('Guest push subscription failed:', err);
    return null;
  }
}

export async function unsubscribeFromPush() {
  if (!swRegistration) return;
  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (subscription) {
      await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
  }
}

// Sound notification using Web Audio API
let audioCtx = null;
export function playNotificationSound() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    // Play a pleasant two-tone chime
    const now = audioCtx.currentTime;
    [523.25, 659.25].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.4);
    });
  } catch (e) {
    // Silently fail on audio context errors
  }
}

// Urgent alarm for escalated/re-raised tickets — louder, triple beep
export function playUrgentAlarmSound() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioCtx.currentTime;
    // Three urgent beeps: high-low-high pattern
    const pattern = [
      { freq: 880, time: 0, dur: 0.15 },
      { freq: 660, time: 0.2, dur: 0.15 },
      { freq: 880, time: 0.4, dur: 0.15 },
      { freq: 660, time: 0.6, dur: 0.15 },
      { freq: 1046.5, time: 0.8, dur: 0.3 },
    ];
    pattern.forEach(({ freq, time, dur }) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.4, now + time);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + dur);
      osc.start(now + time);
      osc.stop(now + time + dur);
    });
  } catch (e) {
    // Silently fail
  }
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export async function getPushSubscriptionStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return { supported: false, permission: 'denied', subscribed: false };
  await initPushNotifications();
  const subscription = await swRegistration?.pushManager.getSubscription();
  return { supported: true, permission: getNotificationPermission(), subscribed: Boolean(subscription) };
}

export async function sendTestPush(api) {
  const resp = await api.post('/push/test');
  return resp.data;
}
